import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import toast from "react-hot-toast";
import { useMainContext } from "../../../../../../context/useMainContext";
import { supabase } from "../../../../../../supabase";

export const CONTENT_TYPE_OPTIONS = [
  { label: "Lifestyle", value: "Lifestyle"},
  { label: "Beauty/Fashion", value: "Beauty/Fashion"},
  { label: "Family", value: "Family"},
  { label: "Fitness", value: "Fitness"},
  { label: "Interior/House", value: "Interior/House"},
  { label: "Animals", value: "Animals", aliases: ["Horses"]},
  { label: "Food", value: "Food"},
  { label: "DIY", value: "DIY"},
];

export const NAIL_SITUATION_OPTIONS = [
  { label: "Has nails", value: "Nature" },
  { label: "Has Colored", value: "Nail Polish" },
  { label: "Not visible", value: "Unclear" },
  { label: "UV Gel/Shellac", value: "UV Gel/Shellac" },
  { label: "Gel/Extensions", value: "Gel/Extensions", aliases: ["Has no nails", "Gel", "Extensions"] },
];

export const TALKS_TO_CAM_OPTIONS = ["Yes", "No", "Sometimes"];

export default function ManualCheck({
  manualCheckModalOpen,
  setManualCheckModalOpen,
}) {
  const { isGuest, setRows, selected, setSelected, saveUndoData, setLoadingData } = useMainContext();
  const influencer = selected.length > 0 ? selected[0] : null;
  const [manualCheckData, setManualCheckData] = useState({
    contentTypeValidation: "",
    nailSituation: "",
    talksToCam: "",
  });

  useEffect(() => {
    if (manualCheckModalOpen && influencer) {
      setManualCheckData({
        contentTypeValidation: influencer.content_type_validation || "",
        nailSituation: influencer.nail_situation || "",
        talksToCam: influencer.talks_to_cam || "",
      });
    }
  }, [manualCheckModalOpen, influencer?.content_type_validation, influencer?.nail_situation, influencer?.talks_to_cam]);

  const handleManualCheckChange = (field, value) => {
    setManualCheckData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submitManualCheck = async () => {
    if (!influencer) return;

    const influencerId = influencer.id;
    const previousData = {
      content_type_validation: influencer.content_type_validation || "",
      nail_situation: influencer.nail_situation || "",
      talks_to_cam: influencer.talks_to_cam || "",
    };

    // Save the updated data for redo
    const redoData = {
      content_type_validation: manualCheckData.contentTypeValidation,
      nail_situation: manualCheckData.nailSituation,
      talks_to_cam: manualCheckData.talksToCam,
    };

    const saveManualCheck = async (data) => {
      try {
        setLoadingData(true);
        
        const saveData = {
          influencer_id: influencerId,
          content_type_validation: data.content_type_validation,
          nail_situation: data.nail_situation,
          talks_to_cam: data.talks_to_cam,
        };

        const { error: upsertError } = await supabase
          .from("internal_reviews")
          .upsert(saveData, { onConflict: "influencer_id" });

        if (upsertError) {
          toast.error(`Failed to save manual check: ${upsertError.message}`);
          return false;
        }

        // Update rows with the new data
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === influencerId
              ? {
                  ...row,
                  content_type_validation: data.content_type_validation,
                  nail_situation: data.nail_situation,
                  talks_to_cam: data.talks_to_cam,
                }
              : row
          )
        );

        // Update selected with the new data
        setSelected((prevSelected) =>
          prevSelected.map((item) =>
            item.id === influencerId
              ? {
                  ...item,
                  content_type_validation: data.content_type_validation,
                  nail_situation: data.nail_situation,
                  talks_to_cam: data.talks_to_cam,
                }
              : item
          )
        );

        return true;
      } finally {
        setLoadingData(false);
      }
    };

    const success = await saveManualCheck(redoData);
    if (success) {
      toast.success("Manual check saved successfully.");

      saveUndoData({
        action: "Manually checked",
        undoFunction: () => saveManualCheck(previousData),
        redoFunction: () => saveManualCheck(redoData),
      });

      // Close the modal after successful save
      setManualCheckModalOpen(false);
    }
  };

  if (!influencer) return null;

  return (
    <Dialog open={manualCheckModalOpen} onClose={() => setManualCheckModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Manual Check – {influencer.username}
        <IconButton onClick={() => setManualCheckModalOpen(false)} size="small" aria-label="Close dialog">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          select
          label="Content Type Validation"
          value={
            CONTENT_TYPE_OPTIONS.find(
              (el) =>
                el.value === manualCheckData.contentTypeValidation ||
                el.label === manualCheckData.contentTypeValidation ||
                el.aliases?.includes(manualCheckData.contentTypeValidation)
            )?.value ||
            manualCheckData.contentTypeValidation ||
            ""
          }
          disabled={isGuest}
          onChange={(e) => handleManualCheckChange("contentTypeValidation", e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
          sx={{ mt: 2 }}
        >
          {CONTENT_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Nail Situation"
          value={
            NAIL_SITUATION_OPTIONS.find(
              (el) =>
                el.value === manualCheckData.nailSituation ||
                el.label === manualCheckData.nailSituation ||
                el.aliases?.includes(manualCheckData.nailSituation)
            )?.value ||
            manualCheckData.nailSituation ||
            ""
          }
          disabled={isGuest}
          onChange={(e) => handleManualCheckChange("nailSituation", e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
        >
          {NAIL_SITUATION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.value}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Talks to Camera"
          value={manualCheckData.talksToCam || ""}
          disabled={isGuest}
          onChange={(e) => handleManualCheckChange("talksToCam", e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
        >
          {TALKS_TO_CAM_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant="outlined"
          onClick={() => setManualCheckModalOpen(false)}
          disableElevation
          aria-label="Cancel manual check for this influencer"
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={() => submitManualCheck()}
          aria-label="Save manual check data for this influencer"
          disableElevation
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
